import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { cn } from "@/lib/utils";
import { GridPattern } from "@/components/ui/grid-pattern";
import { testimonials } from "@/data/testimonials";
import { site } from "@/lib/site";

export function TestimonialsSection() {
  return (
    <section className="relative overflow-hidden bg-background py-24">
      <GridPattern
        width={32}
        height={32}
        className="[mask-image:radial-gradient(ellipse_at_center,white,transparent_75%)]"
      />

      <div className="relative mx-auto max-w-6xl px-5">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-sm font-medium text-primary uppercase tracking-wider">
            Loved by {site.rating.count}+ students
          </p>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-semibold tracking-tight text-balance">
            Real Results, Real Voices
          </h2>
          <p className="mt-4 text-foreground/70 leading-relaxed">
            Real reviews from students who walked in prepared and walked out approved.
            {" "}
            <span className="font-medium text-foreground">{site.rating.score}★ on Trustpilot.</span>
          </p>
        </div>

        <div className="mt-14 grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {testimonials.map((t, i) => {
            const isFeatured = i === 0 || i === 3;
            return (
              <motion.article
                key={`${t.name}-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: isFeatured ? 0.6 : 0.5, delay: i * 0.07 }}
                className={cn(
                  "group relative flex h-full flex-col border border-border bg-card shadow-soft/50 hover:-translate-y-1 hover:shadow-soft transition-all",
                  isFeatured
                    ? "rounded-[2rem] sm:col-span-2 p-8 md:p-10"
                    : i === 1 || i === 5
                      ? "rounded-3xl p-6"
                      : "rounded-3xl p-7"
                )}
              >
                <Quote
                  className={cn(
                    "absolute text-primary/15",
                    isFeatured ? "right-8 top-8 h-10 w-10" : "right-6 top-6 h-8 w-8"
                  )}
                  aria-hidden
                />

                <div className="flex items-center gap-1">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} className="h-4 w-4 fill-gold stroke-gold" />
                  ))}
                </div>

                <h3
                  className={cn(
                    "mt-4 font-display font-semibold leading-snug",
                    isFeatured ? "text-xl md:text-2xl" : "text-lg"
                  )}
                >
                  {t.title}
                </h3>

                <p
                  className={cn(
                    "mt-3 leading-relaxed flex-1",
                    isFeatured ? "text-base text-foreground/70" : "text-sm text-foreground/70"
                  )}
                >
                  "{t.content}"
                </p>

                <div className="mt-6 flex items-center justify-between border-t border-border pt-4 text-xs">
                  <span className="font-medium text-foreground">{t.name}</span>
                  <span className="text-muted-foreground">{t.date}</span>
                </div>
              </motion.article>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <a
            href={site.socials.trustpilot}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
          >
            View all reviews on Trustpilot →
          </a>
        </div>
      </div>
    </section>
  );
}
